// =============================================================================
// PEDRA — src/state/persistence.ts
// Persistencia no navegador: schema VERSIONADO, migracao defensiva, autosave
// com debounce, projetos nomeados e preferencias de interface.
//
// PRINCIPIO: nada aqui lanca excecao para fora. Modo privado, cota estourada,
// JSON corrompido ou dado de outra versao sempre viram um retorno tratado
// (`null` ou `{ ok: false, error }`), nunca um crash da aplicacao.
// =============================================================================

import type {
  EdgeBanding,
  EdgeBandType,
  GrainOrientation,
  Id,
  Part,
  Project,
  Settings,
  StockSheet,
  Unit,
} from '../core/types';
import { PROJECT_VERSION } from '../core/types';
import {
  colorForIndex,
  createDefaultSettings,
  emptyEdgeBanding,
  newId,
  syncIdCounter,
} from '../core/defaults';
import { LIMITS, isValidHexColor } from '../core/validation';

// -----------------------------------------------------------------------------
// CHAVES DE ARMAZENAMENTO
// -----------------------------------------------------------------------------

/**
 * Rascunho atual (autosave). Sempre o ultimo estado editado.
 *
 * v2 desde a correcao dos padroes de fabrica — ver `LEGACY_STORAGE_KEY`.
 */
export const STORAGE_KEY = 'pedra.project.v2';

/**
 * Chave do rascunho da geracao anterior. Lida UMA unica vez, para migrar.
 *
 * POR QUE A MIGRACAO TROCA AS CONFIGURACOES:
 * as `settings` gravadas na v1 nunca foram uma escolha consciente de ninguem —
 * eram o padrao de fabrica de uma versao antiga do app (unidade em milimetro,
 * espessura de corte 4 mm, 1 casa decimal, "considerar material" e "rotulos nas
 * chapas" ligados). Depois os padroes mudaram para espelhar o site que este app
 * replica (tudo desligado, corte 0, metro com 3 casas), mas o que esta salvo
 * tem precedencia sobre o padrao — entao quem ja tinha um rascunho nunca
 * recebeu a correcao e o app passou a se comportar de forma diferente do site.
 * Um projeto de "3,07" lido como 3,07 MILIMETROS com uma serra de 4 mm nao e um
 * projeto: e um bug herdado.
 *
 * Por isso a migracao PRESERVA o trabalho do usuario (nome, pecas, chapas e
 * fitas — isso ele digitou) e ADOTA as configuracoes de fabrica atuais (isso
 * ele nunca escolheu). E ela AVISA: ver `MIGRATION_NOTICE`.
 */
export const LEGACY_STORAGE_KEY = 'pedra.project.v1';

/** Texto exibido pela interface quando a migracao acima acontece. */
export const MIGRATION_NOTICE =
  'As configurações foram atualizadas para os padrões novos (unidade em metro, ' +
  'espessura de corte 0). Suas peças e chapas foram mantidas.';

/** Preferencias de interface (tema, abas, toggles do diagrama). */
export const UI_STORAGE_KEY = 'pedra.ui.v1';

/** Indice dos projetos salvos com nome. */
export const PROJECT_INDEX_KEY = 'pedra.projects.v1';

/** Prefixo de cada projeto nomeado: `pedra.saved.v1.<id>`. */
export const PROJECT_KEY_PREFIX = 'pedra.saved.v1.';

/** Atraso padrao do autosave, em milissegundos. */
export const AUTOSAVE_DELAY_MS = 800;

/** Mesmo digitando sem parar, grava depois deste tempo. */
const AUTOSAVE_MAX_WAIT_MS = 5_000;

/** Teto de projetos nomeados guardados no indice. */
const MAX_SAVED_PROJECTS = 50;

// -----------------------------------------------------------------------------
// ADAPTADOR DE ARMAZENAMENTO (com queda para memoria)
// -----------------------------------------------------------------------------

/** Espelho em memoria usado quando o navegador bloqueia o localStorage. */
const memory = new Map<string, string>();

let storageChecked = false;
let storageWorks = false;

/** Testa uma unica vez se o localStorage esta realmente disponivel. */
function hasLocalStorage(): boolean {
  if (storageChecked) return storageWorks;
  storageChecked = true;
  try {
    const probe = '__pedra_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    storageWorks = true;
  } catch {
    storageWorks = false;
  }
  return storageWorks;
}

/**
 * true quando os dados estao apenas em memoria (aba anonima, ITP do Safari,
 * cookies bloqueados). A interface pode avisar o usuario.
 */
export function isStorageAvailable(): boolean {
  return hasLocalStorage();
}

function readRaw(key: string): string | null {
  if (hasLocalStorage()) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      /* cai para a memoria */
    }
  }
  return memory.has(key) ? (memory.get(key) as string) : null;
}

function writeRaw(key: string, value: string): void {
  if (hasLocalStorage()) {
    // Pode lancar QuotaExceededError — quem chama trata.
    window.localStorage.setItem(key, value);
    return;
  }
  // Sem localStorage os dados vivem so nesta sessao.
  memory.set(key, value);
}

function removeRaw(key: string): void {
  if (hasLocalStorage()) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignora */
    }
  }
  memory.delete(key);
}

/** Reconhece o erro de cota nos varios nomes que os navegadores usam. */
function isQuotaError(e: unknown): boolean {
  if (typeof e !== 'object' || e === null) return false;
  const err = e as { name?: unknown; code?: unknown };
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    err.code === 22 ||
    err.code === 1014
  );
}

// -----------------------------------------------------------------------------
// COERCAO DEFENSIVA
// -----------------------------------------------------------------------------

type Dict = Record<string, unknown>;

function isDict(v: unknown): v is Dict {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asString(v: unknown, fallback: string, maxLength: number): string {
  if (typeof v !== 'string') return fallback;
  return v.length > maxLength ? v.slice(0, maxLength) : v;
}

function asBool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/** Numero inteiro finito dentro de [min, max]; qualquer lixo vira `fallback`. */
function asInt(v: unknown, fallback: number, min: number, max: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  const r = Math.round(n);
  if (r < min) return min;
  if (r > max) return max;
  return r;
}

/** Dimensao em Micron: inteiro >= 0 e <= limite global. */
function asMicron(v: unknown): number {
  return asInt(v, 0, 0, LIMITS.MAX_DIMENSION);
}

function asGrain(v: unknown): GrainOrientation {
  return v === 1 || v === 2 ? v : 0;
}

function asColor(v: unknown, fallback: string): string {
  return typeof v === 'string' && isValidHexColor(v) ? v : fallback;
}

function asIdOrNull(v: unknown): Id | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

function asEnum<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

const UNITS: readonly Unit[] = ['mm', 'cm', 'm', 'in', 'ft'];

function coerceEdgeBanding(v: unknown): EdgeBanding {
  if (!isDict(v)) return emptyEdgeBanding();
  return {
    top: asIdOrNull(v.top),
    right: asIdOrNull(v.right),
    bottom: asIdOrNull(v.bottom),
    left: asIdOrNull(v.left),
  };
}

/**
 * Reserva um id: reaproveita o que veio do arquivo quando for uma string
 * inedita, senao gera um novo. Impede ids duplicados vindos de fora.
 */
function takeId(v: unknown, prefix: string, seen: Set<Id>): Id {
  const raw = typeof v === 'string' ? v.trim() : '';
  if (raw !== '' && raw.length <= 64 && !seen.has(raw)) {
    seen.add(raw);
    return raw;
  }
  let id = newId(prefix);
  while (seen.has(id)) id = newId(prefix);
  seen.add(id);
  return id;
}

function coercePart(raw: unknown, index: number, seen: Set<Id>): Part {
  const d: Dict = isDict(raw) ? raw : {};
  return {
    id: takeId(d.id, 'p', seen),
    length: asMicron(d.length),
    width: asMicron(d.width),
    qty: asInt(d.qty, 1, 0, LIMITS.MAX_QTY),
    label: asString(d.label, '', LIMITS.MAX_LABEL_LENGTH),
    material: asString(d.material, '', LIMITS.MAX_LABEL_LENGTH),
    orientation: asGrain(d.orientation),
    enabled: asBool(d.enabled, true),
    edgeBanding: coerceEdgeBanding(d.edgeBanding),
    color: asColor(d.color, colorForIndex(index)),
  };
}

function coerceStock(raw: unknown, seen: Set<Id>): StockSheet {
  const d: Dict = isDict(raw) ? raw : {};
  const qty =
    d.qty === null || d.qty === undefined || d.qty === ''
      ? null
      : asInt(d.qty, 1, 0, LIMITS.MAX_QTY);
  return {
    id: takeId(d.id, 's', seen),
    length: asMicron(d.length),
    width: asMicron(d.width),
    qty,
    label: asString(d.label, '', LIMITS.MAX_LABEL_LENGTH),
    material: asString(d.material, '', LIMITS.MAX_LABEL_LENGTH),
    orientation: asGrain(d.orientation),
    enabled: asBool(d.enabled, true),
  };
}

function coerceEdgeBand(raw: unknown, seen: Set<Id>): EdgeBandType {
  const d: Dict = isDict(raw) ? raw : {};
  return {
    id: takeId(d.id, 'eb', seen),
    name: asString(d.name, '', LIMITS.MAX_LABEL_LENGTH),
    thickness: asInt(d.thickness, 1_000, 0, LIMITS.MAX_KERF),
    color: asColor(d.color, '#8C8C8C'),
  };
}

function coerceSettings(raw: unknown): Settings {
  const base = createDefaultSettings();
  if (!isDict(raw)) return base;
  const d = raw;
  return {
    kerf: asInt(d.kerf, base.kerf, 0, LIMITS.MAX_KERF),
    considerGrain: asBool(d.considerGrain, base.considerGrain),
    considerMaterial: asBool(d.considerMaterial, base.considerMaterial),
    edgeBandingEnabled: asBool(d.edgeBandingEnabled, base.edgeBandingEnabled),
    labelsOnPanels: asBool(d.labelsOnPanels, base.labelsOnPanels),
    useSingleStockUnit: asBool(d.useSingleStockUnit, base.useSingleStockUnit),
    unit: asEnum(d.unit, UNITS, base.unit),
    dimensionOrder: asEnum(d.dimensionOrder, ['lengthFirst', 'widthFirst'] as const, base.dimensionOrder),
    decimalPlaces: asInt(d.decimalPlaces, base.decimalPlaces, 0, 3),
    minTrimDimension: asMicron(d.minTrimDimension),
    stackSamePanels: asEnum(d.stackSamePanels, ['auto', 'never', 'always'] as const, base.stackSamePanels),
    priority: asEnum(
      d.priority,
      ['leastWaste', 'leastCuts', 'smallerSheets'] as const,
      base.priority,
    ),
    cutOrientation: asEnum(
      d.cutOrientation,
      ['optimal', 'preferWidth', 'preferLength'] as const,
      base.cutOrientation,
    ),
    timeBudgetMs: asInt(d.timeBudgetMs, base.timeBudgetMs, 200, 600_000),
    seed: asInt(d.seed, base.seed, -2_147_483_648, 2_147_483_647),
  };
}

// -----------------------------------------------------------------------------
// MIGRACAO DE SCHEMA
// -----------------------------------------------------------------------------

/**
 * Uma migracao recebe o objeto solto da versao N e devolve o objeto da
 * versao N+1 (ja com `version` atualizado). Sempre para frente, nunca destrutiva.
 * Hoje so existe a versao 1; o mapa fica pronto para quando o schema mudar.
 */
export type ProjectMigration = (raw: Dict) => Dict;

export const MIGRATIONS: Readonly<Record<number, ProjectMigration>> = Object.freeze({});

/**
 * Le um objeto qualquer (localStorage, arquivo JSON, link) e devolve um
 * `Project` valido. NAO confia em nada: campo faltando recebe o padrao, tipo
 * errado e descartado, arrays acima de `LIMITS.MAX_ROWS` sao cortados.
 * Devolve `null` quando o dado nao e um objeto ou quando a versao e FUTURA.
 */
export function migrateProject(raw: unknown): Project | null {
  if (!isDict(raw)) return null;

  // --- versao ----------------------------------------------------------------
  const rawVersion = raw.version;
  let version =
    typeof rawVersion === 'number' && Number.isFinite(rawVersion)
      ? Math.floor(rawVersion)
      : PROJECT_VERSION;
  if (version < 1) version = 1;
  if (version > PROJECT_VERSION) return null; // arquivo de uma versao mais nova

  let cur: Dict = raw;
  while (version < PROJECT_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) return null; // nao ha caminho de atualizacao
    try {
      cur = step(cur);
    } catch {
      return null;
    }
    const next = typeof cur.version === 'number' ? Math.floor(cur.version) : version + 1;
    if (next <= version) return null; // migracao que nao avanca = laco infinito
    version = next;
  }

  // --- tem cara de projeto? --------------------------------------------------
  // Mesmo criterio de `sanitizeProject` (lib/projectFile.ts): sem `parts` nem
  // `stock` como array isto nao e um projeto. Sem esta guarda, um `{}` ou um
  // `{"foo":1}` no localStorage abriria um projeto VAZIO em vez do padrao.
  if (!Array.isArray(cur.parts) && !Array.isArray(cur.stock)) return null;

  // --- sincroniza o contador ANTES de gerar qualquer id novo -----------------
  const existing: Id[] = [];
  const collect = (v: unknown): void => {
    if (!Array.isArray(v)) return;
    for (const item of v) {
      if (isDict(item) && typeof item.id === 'string') existing.push(item.id);
    }
  };
  collect(cur.parts);
  collect(cur.stock);
  collect(cur.edgeBands);
  syncIdCounter(existing);

  // --- conteudo --------------------------------------------------------------
  const seen = new Set<Id>();
  const rawParts = Array.isArray(cur.parts) ? cur.parts.slice(0, LIMITS.MAX_ROWS) : [];
  const rawStock = Array.isArray(cur.stock) ? cur.stock.slice(0, LIMITS.MAX_ROWS) : [];
  const rawBands = Array.isArray(cur.edgeBands) ? cur.edgeBands.slice(0, LIMITS.MAX_ROWS) : [];

  const parts = rawParts.map((p, i) => coercePart(p, i, seen));
  const stock = rawStock.map((s) => coerceStock(s, seen));
  const edgeBands = rawBands.map((b) => coerceEdgeBand(b, seen));

  // Fitas referenciadas que nao existem no cadastro viram "sem fita".
  const bandIds = new Set(edgeBands.map((b) => b.id));
  for (const part of parts) {
    const eb = part.edgeBanding;
    if (eb.top !== null && !bandIds.has(eb.top)) eb.top = null;
    if (eb.right !== null && !bandIds.has(eb.right)) eb.right = null;
    if (eb.bottom !== null && !bandIds.has(eb.bottom)) eb.bottom = null;
    if (eb.left !== null && !bandIds.has(eb.left)) eb.left = null;
  }

  return {
    version: PROJECT_VERSION,
    name: asString(cur.name, 'Novo projeto', LIMITS.MAX_NAME_LENGTH),
    parts,
    stock,
    settings: coerceSettings(cur.settings),
    edgeBands,
  };
}

// -----------------------------------------------------------------------------
// SERIALIZACAO
// -----------------------------------------------------------------------------

/**
 * Copia canonica do projeto: apenas os campos do contrato, na mesma ordem.
 * Evita gravar lixo herdado de versoes antigas ou de importacoes.
 */
function toStorable(p: Project): Project {
  return {
    version: PROJECT_VERSION,
    name: p.name,
    parts: p.parts.map((x) => ({
      id: x.id,
      length: x.length,
      width: x.width,
      qty: x.qty,
      label: x.label,
      material: x.material,
      orientation: x.orientation,
      enabled: x.enabled,
      edgeBanding: { ...x.edgeBanding },
      color: x.color,
    })),
    stock: p.stock.map((x) => ({
      id: x.id,
      length: x.length,
      width: x.width,
      qty: x.qty,
      label: x.label,
      material: x.material,
      orientation: x.orientation,
      enabled: x.enabled,
    })),
    settings: { ...p.settings },
    edgeBands: p.edgeBands.map((b) => ({ ...b })),
  };
}

/** Projeto -> JSON compacto, pronto para gravar. */
export function serializeProject(p: Project): string {
  return JSON.stringify(toStorable(p));
}

/** JSON -> projeto validado. Devolve `null` para texto corrompido. */
export function parseProject(json: string): Project | null {
  if (typeof json !== 'string' || json.trim() === '') return null;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  return migrateProject(raw);
}

// -----------------------------------------------------------------------------
// RASCUNHO ATUAL (AUTOSAVE)
// -----------------------------------------------------------------------------

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Resultado da leitura do rascunho, com o sinal de migracao para a interface. */
export interface LoadedDraft {
  /** O projeto lido, ou `null` quando nao ha rascunho / o dado era ilegivel. */
  project: Project | null;
  /**
   * true quando o rascunho veio da chave v1 e teve as configuracoes
   * substituidas pelos padroes de fabrica atuais. A interface DEVE avisar.
   */
  migrated: boolean;
}

/** Guarda uma copia do dado ilegivel para diagnostico. Nunca lanca. */
function backupCorrupted(key: string, raw: string): void {
  try {
    writeRaw(`${key}.corrompido`, raw.slice(0, 200_000));
  } catch {
    /* sem espaco para o backup: segue em frente */
  }
}

/**
 * Le o rascunho gravado, migrando da chave v1 quando for o caso.
 *
 * Ordem: v2 manda. So quando NAO existe v2 a chave v1 e consultada — e ai ela
 * e consumida (gravamos a v2 e apagamos a v1), para a migracao nunca rodar
 * duas vezes. Nada aqui lanca: dado ilegivel vira `project: null`.
 */
export function loadDraft(): LoadedDraft {
  // --- caminho normal: ja existe a v2 ---------------------------------------
  const raw = readRaw(STORAGE_KEY);
  if (raw !== null) {
    const project = parseProject(raw);
    if (project === null) {
      // Dado ilegivel: guarda uma copia para diagnostico e limpa a chave boa,
      // para o app abrir limpo na proxima vez em vez de falhar sempre.
      backupCorrupted(STORAGE_KEY, raw);
      removeRaw(STORAGE_KEY);
      return { project: null, migrated: false };
    }
    // A v1 que porventura tenha sobrado ja foi superada: nao serve mais.
    removeRaw(LEGACY_STORAGE_KEY);
    return { project, migrated: false };
  }

  // --- migracao v1 -> v2 -----------------------------------------------------
  const legacyRaw = readRaw(LEGACY_STORAGE_KEY);
  if (legacyRaw === null) return { project: null, migrated: false };

  const legacy = parseProject(legacyRaw);
  if (legacy === null) {
    // v1 corrompida ou parcial: o app abre limpo em vez de falhar sempre.
    backupCorrupted(LEGACY_STORAGE_KEY, legacyRaw);
    removeRaw(LEGACY_STORAGE_KEY);
    return { project: null, migrated: false };
  }

  // O trabalho do usuario e sagrado: nome, pecas, chapas e fitas passam
  // intactos. As configuracoes, que ele nunca escolheu, viram as de fabrica.
  const project: Project = {
    ...legacy,
    settings: createDefaultSettings(),
  };

  // Grava a v2 ANTES de descartar a v1: se a escrita falhar (cota, modo
  // anonimo), a v1 continua la e a proxima abertura tenta de novo.
  const written = saveProject(project);
  if (written.ok) removeRaw(LEGACY_STORAGE_KEY);

  return { project, migrated: true };
}

/**
 * Aviso pendente da migracao, entregue UMA vez a quem perguntar.
 * Existe porque quem carrega o rascunho (o reducer do projeto) nao e quem
 * mostra avisos (o App); este e o canal entre os dois, sem estado global novo.
 */
let pendingMigrationNotice: string | null = null;

/**
 * Devolve o aviso da migracao e o consome. `null` quando nao houve migracao
 * ou quando alguem ja leu o aviso. Chame no primeiro efeito da interface.
 */
export function consumeMigrationNotice(): string | null {
  const notice = pendingMigrationNotice;
  pendingMigrationNotice = null;
  return notice;
}

/** Le o rascunho gravado. `null` quando nao ha nada ou o dado esta corrompido. */
export function loadProject(): Project | null {
  const draft = loadDraft();
  // O StrictMode monta duas vezes em desenvolvimento: a segunda leitura ja acha
  // a v2 e nao migra. Por isso o aviso so e ARMADO aqui, nunca desarmado.
  if (draft.migrated) pendingMigrationNotice = MIGRATION_NOTICE;
  return draft.project;
}

/** Grava o rascunho. Nunca lanca: devolve o erro ja em pt-BR. */
export function saveProject(p: Project): { ok: true } | { ok: false; error: string } {
  let json: string;
  try {
    json = serializeProject(p);
  } catch {
    return { ok: false, error: 'Não foi possível preparar o projeto para salvar.' };
  }

  try {
    writeRaw(STORAGE_KEY, json);
    return { ok: true };
  } catch (e) {
    if (isQuotaError(e)) {
      // Libera espaco descartando backups e projetos nomeados mais antigos.
      removeRaw(`${STORAGE_KEY}.corrompido`);
      pruneOldestSavedProjects(1);
      try {
        writeRaw(STORAGE_KEY, json);
        return { ok: true };
      } catch {
        return {
          ok: false,
          error:
            'Não há espaço no navegador para salvar. Exclua projetos salvos ou exporte este em JSON.',
        };
      }
    }
    return {
      ok: false,
      error: 'Não foi possível salvar no navegador. Exporte o projeto em JSON para não perder nada.',
    };
  }
}

/** Apaga o rascunho gravado. */
export function clearStoredProject(): void {
  removeRaw(STORAGE_KEY);
  removeRaw(`${STORAGE_KEY}.corrompido`);
  removeRaw(LEGACY_STORAGE_KEY);
  removeRaw(`${LEGACY_STORAGE_KEY}.corrompido`);
}

// -----------------------------------------------------------------------------
// AUTOSAVE COM DEBOUNCE
// -----------------------------------------------------------------------------

export interface DebouncedSaver {
  /** Marca o projeto como sujo e agenda a gravacao. */
  schedule(p: Project): void;
  /** Grava agora, ignorando o atraso. */
  flush(): void;
  /** Descarta a gravacao pendente. */
  cancel(): void;
}

/**
 * Gravacao adiada: junta varias edicoes seguidas numa unica escrita.
 * Mesmo com digitacao continua, grava a cada `AUTOSAVE_MAX_WAIT_MS`.
 * `onStatus` recebe 'saving' assim que ha algo pendente e depois
 * 'saved' ou 'error' com a mensagem.
 */
export function createDebouncedSaver(
  delayMs: number,
  onStatus: (status: SaveStatus, error?: string) => void,
): DebouncedSaver {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: Project | null = null;
  let firstTouchAt = 0;

  const clearTimer = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const write = (): void => {
    clearTimer();
    const p = pending;
    pending = null;
    firstTouchAt = 0;
    if (p === null) return;
    const res = saveProject(p);
    if (res.ok) onStatus('saved');
    else onStatus('error', res.error);
  };

  return {
    schedule(p: Project): void {
      pending = p;
      const now = Date.now();
      if (firstTouchAt === 0) firstTouchAt = now;
      onStatus('saving');
      clearTimer();
      if (now - firstTouchAt >= AUTOSAVE_MAX_WAIT_MS) {
        write();
        return;
      }
      timer = setTimeout(write, Math.max(0, delayMs));
    },
    flush(): void {
      if (pending !== null) write();
    },
    cancel(): void {
      clearTimer();
      pending = null;
      firstTouchAt = 0;
    },
  };
}

// -----------------------------------------------------------------------------
// PROJETOS NOMEADOS
// -----------------------------------------------------------------------------

/** Resumo leve exibido na lista "Meus projetos" (nunca carrega o projeto todo). */
export interface SavedProjectSummary {
  id: Id;
  name: string;
  /** Data ISO 8601 da ultima gravacao. */
  savedAt: string;
  /** Soma das quantidades das pecas ativas. */
  partCount: number;
  /** Linhas de estoque. */
  stockCount: number;
  /** Tamanho aproximado em bytes, para a tela de gerenciamento de espaco. */
  sizeBytes: number;
}

function coerceSummary(raw: unknown): SavedProjectSummary | null {
  if (!isDict(raw)) return null;
  const id = typeof raw.id === 'string' ? raw.id.trim() : '';
  if (id === '') return null;
  return {
    id,
    name: asString(raw.name, 'Projeto sem nome', LIMITS.MAX_NAME_LENGTH),
    savedAt: typeof raw.savedAt === 'string' ? raw.savedAt : '',
    partCount: asInt(raw.partCount, 0, 0, Number.MAX_SAFE_INTEGER),
    stockCount: asInt(raw.stockCount, 0, 0, Number.MAX_SAFE_INTEGER),
    sizeBytes: asInt(raw.sizeBytes, 0, 0, Number.MAX_SAFE_INTEGER),
  };
}

/** Lista os projetos salvos, do mais recente para o mais antigo. */
export function listSavedProjects(): SavedProjectSummary[] {
  const raw = readRaw(PROJECT_INDEX_KEY);
  if (raw === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    removeRaw(PROJECT_INDEX_KEY);
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: SavedProjectSummary[] = [];
  for (const item of parsed) {
    const s = coerceSummary(item);
    if (s !== null) out.push(s);
  }
  out.sort((a, b) => (a.savedAt < b.savedAt ? 1 : a.savedAt > b.savedAt ? -1 : 0));
  return out;
}

function writeIndex(list: SavedProjectSummary[]): void {
  try {
    writeRaw(PROJECT_INDEX_KEY, JSON.stringify(list.slice(0, MAX_SAVED_PROJECTS)));
  } catch {
    /* indice e acessorio: se nao couber, segue sem ele */
  }
}

/** Descarta os projetos salvos mais antigos para liberar espaco. */
function pruneOldestSavedProjects(count: number): void {
  const list = listSavedProjects();
  if (list.length === 0) return;
  const victims = list.slice(-Math.max(1, count));
  for (const v of victims) removeRaw(PROJECT_KEY_PREFIX + v.id);
  writeIndex(list.filter((s) => !victims.some((v) => v.id === s.id)));
}

/**
 * Salva o projeto com nome. Passe `id` para sobrescrever um projeto existente;
 * sem `id` cria uma entrada nova.
 */
export function saveNamedProject(
  p: Project,
  id?: Id,
): { ok: true; summary: SavedProjectSummary } | { ok: false; error: string } {
  const targetId = id !== undefined && id.trim() !== '' ? id : newId('proj');
  let json: string;
  try {
    json = serializeProject(p);
  } catch {
    return { ok: false, error: 'Não foi possível preparar o projeto para salvar.' };
  }

  const summary: SavedProjectSummary = {
    id: targetId,
    name: p.name.trim() === '' ? 'Projeto sem nome' : p.name.trim(),
    savedAt: new Date().toISOString(),
    partCount: p.parts.reduce((acc, x) => acc + (x.enabled ? x.qty : 0), 0),
    stockCount: p.stock.length,
    sizeBytes: json.length,
  };

  try {
    writeRaw(PROJECT_KEY_PREFIX + targetId, json);
  } catch (e) {
    if (isQuotaError(e)) {
      pruneOldestSavedProjects(2);
      try {
        writeRaw(PROJECT_KEY_PREFIX + targetId, json);
      } catch {
        return {
          ok: false,
          error:
            'Não há espaço no navegador para salvar mais projetos. Exclua algum da lista ou exporte em JSON.',
        };
      }
    } else {
      return { ok: false, error: 'Não foi possível salvar o projeto neste navegador.' };
    }
  }

  const list = listSavedProjects().filter((s) => s.id !== targetId);
  list.unshift(summary);
  if (list.length > MAX_SAVED_PROJECTS) {
    for (const extra of list.slice(MAX_SAVED_PROJECTS)) {
      removeRaw(PROJECT_KEY_PREFIX + extra.id);
    }
  }
  writeIndex(list);
  return { ok: true, summary };
}

/** Carrega um projeto salvo pelo id. `null` se nao existe ou esta corrompido. */
export function loadNamedProject(id: Id): Project | null {
  const raw = readRaw(PROJECT_KEY_PREFIX + id);
  if (raw === null) return null;
  const project = parseProject(raw);
  if (project === null) {
    deleteNamedProject(id);
    return null;
  }
  return project;
}

/** Exclui um projeto salvo e o tira do indice. */
export function deleteNamedProject(id: Id): void {
  removeRaw(PROJECT_KEY_PREFIX + id);
  writeIndex(listSavedProjects().filter((s) => s.id !== id));
}

/** Renomeia um projeto salvo. Devolve false quando o id nao existe. */
export function renameNamedProject(id: Id, name: string): boolean {
  const list = listSavedProjects();
  const found = list.find((s) => s.id === id);
  if (!found) return false;
  const clean = name.trim().slice(0, LIMITS.MAX_NAME_LENGTH);
  found.name = clean === '' ? 'Projeto sem nome' : clean;
  const project = loadNamedProject(id);
  if (project !== null) {
    project.name = found.name;
    try {
      writeRaw(PROJECT_KEY_PREFIX + id, serializeProject(project));
    } catch {
      /* o nome no indice ja basta para a lista */
    }
  }
  writeIndex(list);
  return true;
}

// -----------------------------------------------------------------------------
// PREFERENCIAS DE INTERFACE
// -----------------------------------------------------------------------------

/** Preferencias de interface (tema, aba ativa, largura da sidebar, toggles). */
export interface UiPrefs {
  theme: 'light' | 'dark';
  sidebarWidth: number;
  activeTab: string;
  showLabels: boolean;
  showDimensions: boolean;
  showCuts: boolean;
  colorFilled: boolean;
}

export const DEFAULT_UI_PREFS: UiPrefs = {
  theme: 'light',
  sidebarWidth: 420,
  activeTab: 'diagram',
  showLabels: true,
  showDimensions: true,
  showCuts: false,
  colorFilled: true,
};

/** Le as preferencias. Sempre devolve um objeto completo. */
export function loadUiPrefs(): UiPrefs {
  const raw = readRaw(UI_STORAGE_KEY);
  if (raw === null) return { ...DEFAULT_UI_PREFS };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    removeRaw(UI_STORAGE_KEY);
    return { ...DEFAULT_UI_PREFS };
  }
  if (!isDict(parsed)) return { ...DEFAULT_UI_PREFS };
  return {
    theme: asEnum(parsed.theme, ['light', 'dark'] as const, DEFAULT_UI_PREFS.theme),
    sidebarWidth: asInt(parsed.sidebarWidth, DEFAULT_UI_PREFS.sidebarWidth, 320, 720),
    activeTab: asString(parsed.activeTab, DEFAULT_UI_PREFS.activeTab, 40),
    showLabels: asBool(parsed.showLabels, DEFAULT_UI_PREFS.showLabels),
    showDimensions: asBool(parsed.showDimensions, DEFAULT_UI_PREFS.showDimensions),
    showCuts: asBool(parsed.showCuts, DEFAULT_UI_PREFS.showCuts),
    colorFilled: asBool(parsed.colorFilled, DEFAULT_UI_PREFS.colorFilled),
  };
}

/** Grava as preferencias. Falha silenciosa: preferencia nao vale um erro. */
export function saveUiPrefs(p: UiPrefs): void {
  try {
    writeRaw(UI_STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignora */
  }
}
